import React from 'react';
import { Box, Link, useMediaQuery, useTheme } from '@mui/material';

/**
 * Компонент рекламного баннера
 * 
 * Для настройки баннера отредактируйте файл adBannerConfig.js
 * 
 * Требования к баннеру:
 * - Формат: JPG, PNG или SVG
 * - Рекомендуемые размеры: 
 *   * Десктоп: 728x90 (стандартный баннер) или 300x250 (боковой баннер)
 *   * Мобильный: 320x100 или 300x250
 * - Максимальный размер файла: 200KB (для быстрой загрузки)
 * - Изображение должно быть оптимизировано для веба
 */
const AdBanner = ({ config }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Если баннер отключен, не показываем компонент
  if (!config || !config.enabled) {
    return null;
  }

  // Определяем, какое изображение использовать
  // Если есть мобильная версия и мы на мобильном устройстве - используем её
  // Иначе используем основное изображение
  const imageUrl = isMobile && config.mobileImageUrl 
    ? config.mobileImageUrl 
    : config.imageUrl;

  // Если нет изображения, не показываем компонент
  if (!imageUrl) {
    return null;
  }

  // Определяем размеры для лучшего качества
  const getBannerDimensions = () => {
    if (isMobile && config.mobileImageUrl) {
      // Для мобильного баннера используем его реальные размеры (320x100)
      // Используем фиксированные размеры для максимального качества
      return {
        width: '320px',
        maxWidth: '100%',
        height: '100px', // Фиксированная высота для сохранения пропорций
      };
    } else {
      // Для десктопного баннера используем его реальные размеры
      return {
        width: '720px',
        maxWidth: '100%',
        height: 'auto',
      };
    }
  };

  const dimensions = getBannerDimensions();

  const bannerContent = (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        margin: isMobile ? '16px 0' : '20px 0',
        padding: isMobile ? 0 : '10px', // Убираем padding для мобильных
        backgroundColor: isMobile ? 'transparent' : (config.backgroundColor || '#f5f5f5'), // Прозрачный фон для мобильных
        borderRadius: isMobile ? 0 : '8px', // Убираем скругление для мобильных
        overflow: 'hidden',
        border: 'none', // Явно убираем границы
        boxShadow: 'none', // Убираем тени
        '& img': {
          display: 'block',
          border: 'none', // Убираем границы у изображения
          outline: 'none', // Убираем outline
          // Оптимизация для четкости на мобильных устройствах
          imageRendering: isMobile ? 'crisp-edges' : 'high-quality',
          WebkitImageRendering: isMobile ? '-webkit-optimize-contrast' : 'high-quality',
          // Предотвращаем размытие при масштабировании
          backfaceVisibility: 'hidden',
          transform: 'translateZ(0)', // Аппаратное ускорение
          WebkitFontSmoothing: 'antialiased',
        },
      }}
    >
      <img
        src={imageUrl}
        alt={config.altText || 'Рекламний банер'}
        style={{
          width: isMobile 
            ? (config.width === 'auto' ? dimensions.width : config.width || dimensions.width)
            : (config.width === 'auto' ? dimensions.width : config.width || dimensions.width),
          maxWidth: dimensions.maxWidth,
          height: isMobile 
            ? (config.height === 'auto' ? dimensions.height : config.height || dimensions.height)
            : (config.height === 'auto' ? 'auto' : config.height || dimensions.height),
          objectFit: 'contain', // Сохраняем пропорции
          // Оптимизация качества для мобильных - используем crisp-edges для максимальной четкости
          imageRendering: isMobile ? 'crisp-edges' : 'high-quality',
          WebkitImageRendering: isMobile ? '-webkit-optimize-contrast' : 'high-quality',
          border: 'none', // Явно убираем границы
          outline: 'none', // Убираем outline
        }}
        loading="lazy"
      />
    </Box>
  );

  // Если указана ссылка, оборачиваем в Link
  if (config.linkUrl) {
    return (
      <Link
        href={config.linkUrl}
        target={config.openInNewTab ? '_blank' : '_self'}
        rel={config.openInNewTab ? 'noopener noreferrer' : undefined}
        sx={{
          textDecoration: 'none',
          display: 'block',
          border: 'none', // Убираем границы у ссылки
          outline: 'none', // Убираем outline
          '&:focus': {
            outline: 'none',
            border: 'none',
          },
        }}
      >
        {bannerContent}
      </Link>
    );
  }

  return bannerContent;
};

export default AdBanner;
